// Mock data for the AI Agent Audit Dashboard.
// Shaped to match the ESSMS/BMS platform (25 services) and the essms-mbo
// service specifically, derived from horolab-service-docs/*/CLAUDE.md.
// Replace with a real fetch() against the pipeline's audit API once available.

const SERVICE_DEFS = [
  { id: 'essms-auth', name: 'essms-auth', type: 'identity', port: 9100, description: 'IdP: OIDC/OAuth2 + JWT, users/roles/branches, RBAC/CBAC', deps: [] },
  { id: 'essms-proxy', name: 'essms-proxy', type: 'gateway', port: 9102, description: 'API gateway: JWT validation, context headers, CBAC authorization', deps: ['essms-auth'] },
  { id: 'essms-admin', name: 'essms-admin', type: 'core', port: 9103, description: 'Master/reference data (geography, partners, org, config)', deps: ['essms-proxy'] },
  { id: 'essms-repair', name: 'essms-repair', type: 'domain', port: 9104, description: 'Repair job lifecycle, product-receive, kiosks, techsheets', deps: ['essms-proxy', 'essms-admin'] },
  { id: 'essms-inventory', name: 'essms-inventory', type: 'domain', port: 9105, description: 'Parts/products, stock, orders, purchase orders', deps: ['essms-proxy', 'essms-admin'] },
  { id: 'essms-invoice', name: 'essms-invoice', type: 'domain', port: 9106, description: 'Invoicing/billing, GST numbering, Jasper PDF, GSTR reports', deps: ['essms-proxy', 'essms-inventory'] },
  { id: 'essms-courier', name: 'essms-courier', type: 'domain', port: 9107, description: 'Courier/collection-agent last-mile delivery, proof-of-pickup', deps: ['essms-proxy', 'essms-repair'] },
  { id: 'essms-money', name: 'essms-money', type: 'domain', port: 9108, description: 'Kiosk physical cash handling + investor master', deps: ['essms-proxy'] },
  { id: 'essms-notification', name: 'essms-notification', type: 'core', port: 9109, description: 'Notifications (email only today; SMS/push stubbed)', deps: ['essms-proxy'] },
  { id: 'essms-mbo', name: 'essms-mbo', type: 'domain', port: 9110, description: 'MBO: projects/objectives/forms + HR-support. Deployed twice (essms-mbo + bms-mbo)', deps: ['essms-auth', 'essms-proxy', 'essms-admin', 'essms-notification'] },
  { id: 'bms-accounting', name: 'bms-accounting', type: 'domain', port: 9111, description: 'Invoices/expenses/payments + KPI dashboard', deps: ['essms-proxy'] },
  { id: 'bms-hrms', name: 'bms-hrms', type: 'domain', port: 9112, description: 'HRMS: only Attendance + Leave implemented', deps: ['essms-proxy'] },
  { id: 'essms-ui', name: 'essms-ui', type: 'frontend', port: 80, description: 'ESSMS Angular SPA (dev.horolab.in)', deps: ['essms-proxy'] },
  { id: 'bms-ui', name: 'bms-ui', type: 'frontend', port: 80, description: 'WhiteBirrd portal Angular SPA (dev.whitebirrd.com)', deps: ['essms-proxy', 'essms-mbo'] },
  { id: 'essms-bot', name: 'essms-bot', type: 'domain', port: 8080, description: 'Chatflow/WhatsApp bot backend (Spring Boot 3.1.5)', deps: ['essms-proxy'] },
  { id: 'essms-bot-ui', name: 'essms-bot-ui', type: 'frontend', port: 80, description: 'React chatflow builder UI', deps: ['essms-bot'] },
]

const STATUS_BY_ID = {
  'essms-money': 'degraded', // k8s manifests use wrong port (9105 vs 9108) per PLATFORM-OVERVIEW.md
  'bms-accounting': 'degraded', // gateway route broken in every env (services.accounting.url undefined)
  'bms-hrms': 'degraded', // only Attendance+Leave implemented, /Leave/listwithforms returns empty
  'essms-bot': 'degraded', // committed live API keys, hardcoded signing keys
}

const services = {
  nodes: SERVICE_DEFS.map(s => ({
    id: s.id,
    name: s.name,
    type: s.type,
    port: s.port,
    description: s.description,
    status: STATUS_BY_ID[s.id] || 'healthy',
    dependencies: s.deps,
  })),
}

// ---------------------------------------------------------------------------
// Pipeline status — one row per service per environment
// ---------------------------------------------------------------------------

function makePipeline(id, service, environment, status, branch, commit, duration, lastRunMinsAgo) {
  return {
    id,
    service,
    environment,
    status,
    branch,
    commit,
    duration,
    lastRun: new Date(Date.now() - lastRunMinsAgo * 60000).toISOString(),
  }
}

const pipeline = [
  makePipeline(1, 'essms-mbo', 'dev', 'passed', 'main', 'a3f5c9e12b8d4f61', 187, 12),
  makePipeline(2, 'essms-mbo', 'qa', 'passed', 'main', 'a3f5c9e12b8d4f61', 203, 45),
  makePipeline(3, 'essms-mbo', 'prod', 'pending', 'main', '7d2e8a41c0f93b56', 0, 180),
  makePipeline(4, 'essms-auth', 'dev', 'passed', 'main', 'e91b3f2a7c6d0158', 156, 22),
  makePipeline(5, 'essms-auth', 'qa', 'passed', 'main', 'e91b3f2a7c6d0158', 168, 60),
  makePipeline(6, 'essms-auth', 'prod', 'passed', 'main', 'e91b3f2a7c6d0158', 174, 720),
  makePipeline(7, 'essms-proxy', 'dev', 'passed', 'main', 'b45c8d9f1e2a3067', 98, 15),
  makePipeline(8, 'essms-proxy', 'qa', 'failed', 'main', 'b45c8d9f1e2a3067', 42, 33),
  makePipeline(9, 'essms-proxy', 'prod', 'passed', 'main', 'f01a9e3c5b7d2846', 105, 1440),
  makePipeline(10, 'essms-admin', 'dev', 'passed', 'main', 'c78d2e0a4f9b1635', 121, 8),
  makePipeline(11, 'essms-admin', 'qa', 'passed', 'main', 'c78d2e0a4f9b1635', 130, 90),
  makePipeline(12, 'essms-repair', 'dev', 'running', 'main', '9f4b7c1e8a2d0563', 0, 2),
  makePipeline(13, 'essms-repair', 'qa', 'passed', 'main', '5e0d9a3f7c1b8642', 145, 120),
  makePipeline(14, 'essms-inventory', 'dev', 'passed', 'main', 'd23a8f6b0e4c1957', 132, 18),
  makePipeline(15, 'essms-inventory', 'qa', 'passed', 'main', 'd23a8f6b0e4c1957', 140, 100),
  makePipeline(16, 'essms-invoice', 'dev', 'passed', 'main', '6b1e4c9a2f7d0385', 112, 25),
  makePipeline(17, 'essms-courier', 'dev', 'passed', 'main', '2a7f9c3e5b8d1064', 88, 30),
  makePipeline(18, 'essms-money', 'dev', 'failed', 'main', '8d3b6a0f1e9c4257', 34, 55),
  makePipeline(19, 'essms-notification', 'dev', 'passed', 'main', '4c9e2a7f0b6d1358', 76, 40),
  makePipeline(20, 'bms-accounting', 'dev', 'failed', 'main', '1f8a5c2e9b3d0746', 28, 66),
  makePipeline(21, 'bms-hrms', 'dev', 'passed', 'main', '3e0b7d4a8f2c1659', 94, 50),
  makePipeline(22, 'essms-ui', 'dev', 'passed', 'main', '7c2f9e5a1b8d3064', 210, 20),
  makePipeline(23, 'bms-ui', 'dev', 'passed', 'main', '0a4d8b3f6e1c9527', 198, 28),
]

const pipelineHistory = Array.from({ length: 30 }, (_, i) => {
  const date = new Date(Date.now() - (29 - i) * 86400000)
  const base = 78 + Math.sin(i / 4) * 8
  const noise = (Math.sin(i * 1.7) + Math.cos(i * 0.9)) * 4
  return {
    date: date.toISOString().slice(0, 10),
    successRate: Math.max(50, Math.min(100, Math.round(base + noise))),
  }
})

// ---------------------------------------------------------------------------
// Scope derivation — ScopeDerivation.jsx builds its own tree internally;
// this is kept as a lightweight source-provenance summary for parity.
// ---------------------------------------------------------------------------

const scope = {
  sourcesUsed: [
    'horolab-service-docs/essms-mbo/CLAUDE.md',
    'horolab-service-docs/PLATFORM-OVERVIEW.md',
    'horolab-service-docs/*/CLAUDE.md (14 services)',
  ],
  generatedAt: new Date(Date.now() - 3 * 3600000).toISOString(),
}

// ---------------------------------------------------------------------------
// Test coverage — synthetic JaCoCo-style file coverage across MBO packages
// ---------------------------------------------------------------------------

const MBO_PACKAGES = [
  'controllers', 'services', 'repository', 'entities', 'forms', 'utils', 'configs', 'security',
]

const MBO_FILES_BY_PACKAGE = {
  controllers: ['ProjectController', 'ObjectiveController', 'ProcessController', 'RequirementController', 'IssueController', 'AnalysisController', 'DashboardController', 'DefaultController', 'MboItemsAdminController', 'SchemaProvisionController', 'LeaveController', 'HolidayController'],
  services: ['ProjectServiceImpl', 'ObjectiveServiceImpl', 'ProcessServiceImpl', 'DashboardServiceImpl', 'CrossTenantMboItemServiceImpl', 'RedisTokenService', 'NotificationService'],
  repository: ['ProjectItemRepository', 'ObjectiveRepository', 'CrossSchemaProjectItemRepository', 'TenantSchemaRepository', 'SchemaValidator', 'ProcessRepository'],
  entities: ['ProjectItem', 'Objective', 'Process', 'Requirement', 'Issue', 'Analysis', 'Ledger'],
  forms: ['Form', 'FormEntry', 'FormList', 'ObjectiveForm', 'ProjectItemForm'],
  utils: ['DateUtil', 'ExcelImportUtil', 'JwtTokenUtil', 'MapperUtil'],
  configs: ['ResourceServerConfig', 'RabbitMQConfig', 'LiquibaseConfig', 'SwaggerConfig'],
  security: ['UserPrincipal', 'TokenBlacklistFilter'],
}

function seededRandom(seed) {
  const x = Math.sin(seed) * 10000
  return x - Math.floor(x)
}

let fileSeed = 1
const coverageFiles = []
Object.entries(MBO_FILES_BY_PACKAGE).forEach(([pkg, files]) => {
  files.forEach(file => {
    fileSeed += 1
    const lines = 60 + Math.floor(seededRandom(fileSeed) * 240)
    const lineCoverage = Math.round((45 + seededRandom(fileSeed * 2) * 52) * 10) / 10
    const branchCoverage = Math.round((35 + seededRandom(fileSeed * 3) * 55) * 10) / 10
    const coveredLines = Math.round(lines * (lineCoverage / 100))
    coverageFiles.push({
      path: `src/main/java/com/bms/mbo/${pkg}/${file}.java`,
      lines,
      coveredLines,
      lineCoverage,
      branchCoverage,
      complexity: Math.round(seededRandom(fileSeed * 5) * 100) / 10,
    })
  })
})

const totalLines = coverageFiles.reduce((sum, f) => sum + f.lines, 0)
const totalCovered = coverageFiles.reduce((sum, f) => sum + f.coveredLines, 0)

const coverage = {
  summary: {
    lineCoverage: Math.round((totalCovered / totalLines) * 1000) / 10,
    branchCoverage: Math.round((coverageFiles.reduce((s, f) => s + f.branchCoverage, 0) / coverageFiles.length) * 10) / 10,
    totalFiles: coverageFiles.length,
    totalLines,
    coveredLines: totalCovered,
    totalTests: 342,
    passedTests: 318,
  },
  files: coverageFiles,
}

const testHistory = Array.from({ length: 14 }, (_, i) => {
  const total = 320 + Math.floor(seededRandom(i + 100) * 40)
  const failed = Math.floor(seededRandom(i + 200) * 12)
  const skipped = Math.floor(seededRandom(i + 300) * 6)
  return {
    run: i + 1,
    passed: total - failed - skipped,
    failed,
    skipped,
  }
})

// ---------------------------------------------------------------------------
// Security findings — SpotBugs / PMD / OWASP Dependency Check / secret scan
// ---------------------------------------------------------------------------

const security = {
  findings: [
    { severity: 'critical', tool: 'gitleaks', rule: 'generic-api-key', file: 'src/main/resources/application-dev.properties', line: 14, description: 'Hardcoded DB password "serverbird@135" committed to repo', status: 'open' },
    { severity: 'critical', tool: 'gitleaks', rule: 'hardcoded-secret', file: 'src/main/resources/application.properties', line: 47, description: 'internal.api.secret hardcoded, shared with essms-auth', status: 'open' },
    { severity: 'high', tool: 'spotbugs', rule: 'DM_SYSOUT', file: 'src/main/java/com/bms/mbo/controllers/ProjectController.java', line: 88, description: 'JWT claim parse errors swallowed via System.err.println instead of proper logging', status: 'open' },
    { severity: 'high', tool: 'dependency-check', rule: 'CVE-2024-22243', file: 'pom.xml', line: 62, description: 'spring-web transitive dependency vulnerable to URL parsing SSRF (CVSS 7.5)', status: 'open' },
    { severity: 'high', tool: 'spotbugs', rule: 'HARD_CODE_PASSWORD', file: 'src/main/resources/application.properties', line: 47, description: 'internal.api.secret detected as hardcoded credential', status: 'open' },
    { severity: 'medium', tool: 'pmd', rule: 'AvoidCatchingGenericException', file: 'src/main/java/com/bms/mbo/controllers/ObjectiveController.java', line: 134, description: 'Generic Exception caught, masking real JWT parsing failures', status: 'open' },
    { severity: 'medium', tool: 'spotbugs', rule: 'EI_EXPOSE_REP', file: 'src/main/java/com/bms/mbo/entities/ProjectItem.java', line: 41, description: 'currentStatus byte[] bitmap field exposes internal representation', status: 'suppressed' },
    { severity: 'medium', tool: 'pmd', rule: 'GodClass', file: 'src/main/java/com/bms/mbo/services/impl/ProjectServiceImpl.java', line: 1, description: 'Class has grown to 900+ lines mixing CRUD, tree, and access-control logic', status: 'open' },
    { severity: 'medium', tool: 'dependency-check', rule: 'CVE-2023-34455', file: 'pom.xml', line: 118, description: 'snappy-java transitive dependency, denial-of-service via crafted input', status: 'fixed' },
    { severity: 'low', tool: 'checkstyle', rule: 'MagicNumber', file: 'src/main/java/com/bms/mbo/utils/DateUtil.java', line: 27, description: 'Magic number 86400000 used for day-in-ms without named constant', status: 'open' },
    { severity: 'low', tool: 'spotbugs', rule: 'DM_PRINT_STACK_TRACE', file: 'src/main/java/com/bms/mbo/controllers/ProcessController.java', line: 59, description: 'printStackTrace() used instead of logger', status: 'open' },
    { severity: 'low', tool: 'pmd', rule: 'UnusedImports', file: 'src/main/java/com/bms/mbo/forms/ObjectiveForm.java', line: 3, description: 'Unused import of com.fasterxml.jackson.annotation.JsonIgnore', status: 'fixed' },
    { severity: 'info', tool: 'pmd', rule: 'CommentRequired', file: 'src/main/java/com/bms/mbo/services/ProcessService.java', line: 1, description: 'Public interface missing class-level Javadoc', status: 'suppressed' },
    { severity: 'info', tool: 'checkstyle', rule: 'LineLength', file: 'src/main/java/com/bms/mbo/entities/Objective.java', line: 76, description: 'Line exceeds 120 characters', status: 'open' },
  ],
}

// ---------------------------------------------------------------------------
// Performance baselines — k6 smoke/load run against the MBO API surface
// ---------------------------------------------------------------------------

const performance = {
  summary: {
    avgP95: 312,
    maxP95: 1180,
    endpointCount: 10,
    testRuns: 24,
  },
  endpoints: [
    { endpoint: '/projectItem/item', method: 'GET', p50: 45, p90: 120, p95: 165, p99: 310, throughput: 42, errorRate: 0.002 },
    { endpoint: '/projectItem/item', method: 'POST', p50: 88, p90: 210, p95: 290, p99: 480, throughput: 18, errorRate: 0.004 },
    { endpoint: '/projectItem/create', method: 'POST', p50: 130, p90: 340, p95: 450, p99: 720, throughput: 12, errorRate: 0.008 },
    { endpoint: '/projectItem/getForm', method: 'GET', p50: 22, p90: 55, p95: 78, p99: 140, throughput: 55, errorRate: 0.000 },
    { endpoint: '/projectItem/uploadExcel', method: 'POST', p50: 620, p90: 1050, p95: 1180, p99: 1850, throughput: 3, errorRate: 0.021 },
    { endpoint: '/objective/create', method: 'POST', p50: 95, p90: 230, p95: 310, p99: 510, throughput: 15, errorRate: 0.005 },
    { endpoint: '/objective/statusUpdate', method: 'POST', p50: 70, p90: 175, p95: 240, p99: 400, throughput: 20, errorRate: 0.003 },
    { endpoint: '/objective/getListOfAccessForUser/{userId}', method: 'GET', p50: 38, p90: 95, p95: 130, p99: 220, throughput: 30, errorRate: 0.001 },
    { endpoint: '/process/getProcess', method: 'GET', p50: 28, p90: 68, p95: 92, p99: 160, throughput: 25, errorRate: 0.000 },
    { endpoint: '/dashboard/view', method: 'GET', p50: 55, p90: 140, p95: 195, p99: 340, throughput: 28, errorRate: 0.002 },
  ],
}

// ---------------------------------------------------------------------------
// Architecture decisions
// ---------------------------------------------------------------------------

const architecture = {
  adrs: [
    {
      id: 1,
      status: 'accepted',
      date: '2026-07-20',
      title: 'Test MBO locally via docker-compose mock stack instead of dev/qa',
      description: 'All test execution runs against a fully local stack (Postgres, Redis, RabbitMQ, mock essms-auth, mock essms-proxy) to avoid touching shared dev/qa/prod environments.',
      context: 'The user explicitly requested no connections to Dev, QA, or Prod during pipeline development. essms-mbo depends on essms-auth (JWKS) and essms-proxy (gateway) at runtime, both of which are shared platform services.',
      decision: 'Build lightweight mock implementations of essms-auth (JWKS + provisioning) and essms-proxy (routing + header relay) as separate Spring Boot apps in local-stack/mocks/, orchestrated via docker-compose.local.yml alongside real Postgres/Redis/RabbitMQ containers.',
      consequences: [
        'Full test isolation — no risk to shared dev/qa/prod state',
        'Mock JWKS uses a locally generated RSA keypair; tokens are not validated against real essms-auth logic, so auth edge cases still need occasional dev validation',
        'Contract tests double as documentation for the mock/real boundary',
      ],
      tags: ['testing', 'local-stack', 'security'],
      author: 'AI Architect Agent',
    },
    {
      id: 2,
      status: 'accepted',
      date: '2026-07-21',
      title: 'Use TestContainers PostgreSQL for integration tests, not the docker-compose Postgres',
      description: 'Integration tests spin up their own ephemeral PostgreSQL container via TestContainers rather than sharing the docker-compose Postgres instance.',
      context: 'Sharing a single long-lived Postgres instance across test runs risks data pollution between test classes and flaky parallel test execution.',
      decision: 'Every *IntegrationTest class declares its own @Container PostgreSQLContainer with create-drop DDL and a fresh Liquibase run, isolated per test class.',
      consequences: [
        'Slower test startup (~3-5s per test class) but full isolation',
        'CI runners need Docker-in-Docker or a Docker socket mount',
        'The docker-compose Postgres remains useful for manual/interactive local testing',
      ],
      tags: ['testing', 'testcontainers', 'ci'],
      author: 'AI Architect Agent',
    },
    {
      id: 3,
      status: 'accepted',
      date: '2026-07-22',
      title: 'Force spring.jpa.hibernate.ddl-auto=validate in all test/local profiles',
      description: 'Override the risky common ddl-auto=create setting from essms-mbo/application.properties in every local and test profile.',
      context: 'PLATFORM-OVERVIEW.md flags spring.jpa.hibernate.ddl-auto=create in the common properties with no environment override as a data-loss risk — Hibernate can silently recreate tables at boot alongside Liquibase.',
      decision: 'All local-stack and test configuration explicitly sets ddl-auto=validate, so schema drift is caught as a startup failure instead of silently applied.',
      consequences: [
        'Any entity/schema mismatch now fails fast at boot instead of corrupting data',
        'Liquibase changelogs become the single source of truth for schema in every non-dev environment',
        'Does not fix the underlying dev/qa/prod risk — a platform-level fix is still recommended',
      ],
      tags: ['database', 'liquibase', 'risk-mitigation'],
      author: 'AI Architect Agent',
    },
    {
      id: 4,
      status: 'accepted',
      date: '2026-07-23',
      title: 'Adopt k6 over JMeter for MBO performance testing',
      description: 'Standardize on k6 scripts (smoke/load/stress) as the primary performance testing tool, keeping JMeter only for compatibility with existing platform pipelines.',
      context: 'k6 scripts are version-controllable JavaScript, integrate cleanly with CI, and produce structured JSON output; JMeter XML test plans are harder to diff and review.',
      decision: 'test-artifacts/performance/k6/ holds smoke.js, load.js, stress.js, main.js as the canonical suite. JMeter remains available under bitbucket-pipelines.yml / cloudbuild-aws.yaml compatibility paths only.',
      consequences: [
        'CI-friendly, scriptable performance gates on p95/p99 latency',
        'Team needs baseline JS familiarity to extend test scripts',
        'JMeter .jmx files may drift out of sync if not actively maintained',
      ],
      tags: ['performance', 'k6', 'ci'],
      author: 'AI Architect Agent',
    },
    {
      id: 5,
      status: 'proposed',
      date: '2026-07-25',
      title: 'Introduce a shared JwtClaimsExtractor to replace repeated manual JWT parsing',
      description: 'Consolidate the inline, repetitive JWT claim extraction (userGUID, roleGUIDs, tenantId, isSuperAdmin) scattered across controllers into a single reusable component.',
      context: 'CLAUDE.md notes "JWT parsing is inline and repetitive" — most controllers manually pull claims from jwt.getClaims() and swallow parse errors with System.out/System.err prints, which both SpotBugs and PMD flag.',
      decision: 'Proposed: extract a JwtClaimsExtractor bean (or use the existing UserPrincipal more consistently) so claim parsing and error handling happen in exactly one place with proper logging.',
      consequences: [
        'Eliminates ~12 duplicated try/catch blocks across controllers',
        'Requires touching every controller — a coordinated, reviewed change rather than a drive-by fix',
        'Not yet implemented; flagged here as a recommendation from the security/quality scan',
      ],
      tags: ['security', 'refactor', 'quality'],
      author: 'AI Architect Agent',
    },
    {
      id: 6,
      status: 'accepted',
      date: '2026-07-26',
      title: 'Mock essms-proxy relays X-TENANT-ID/X-USER-GUID/X-ROLE-GUIDS headers, does not re-derive them',
      description: 'The local mock gateway forwards or defaults the same header contract the real essms-proxy JwtTokenRelayFilter produces, rather than reimplementing full JWT-to-header translation logic.',
      context: 'essms-mbo trusts X-* headers set by essms-proxy and re-validates the JWT itself against JWKS. Faithfully mocking the full CBAC AuthorizationFilter chain was out of scope for local testing.',
      decision: 'MockGatewayConfig defaults X-TENANT-ID to "default" and injects mock user/role headers when absent, letting downstream contract tests exercise the same header contract without full CBAC.',
      consequences: [
        'Local tests validate MBO business logic and multi-tenancy, not the real CBAC authorization rules',
        'CBAC-specific behavior (permission checks against essms-auth /userpermission/check) must still be validated against dev',
        'Kept intentionally simple to avoid scope creep into essms-proxy internals',
      ],
      tags: ['auth', 'local-stack', 'gateway'],
      author: 'AI Architect Agent',
    },
  ],
}

// ---------------------------------------------------------------------------
// Recent AI agent activity feed (Overview page)
// ---------------------------------------------------------------------------

const recentActivity = [
  { timestamp: new Date(Date.now() - 8 * 60000).toISOString(), type: 'success', title: 'Contract tests generated for /objective endpoints', description: 'Created ObjectiveContractTest.java covering create, update, statusUpdate, extension/denial forms, and access control (9 scenarios).', agent: 'Test Engineer', duration: '2m 14s' },
  { timestamp: new Date(Date.now() - 34 * 60000).toISOString(), type: 'info', title: 'Parsed 14 service CLAUDE.md files', description: 'Built cross-service dependency graph from horolab-service-docs/*/CLAUDE.md — 25 services, 84 endpoints, 12 inter-service dependencies mapped.', agent: 'Requirements Analyst', duration: '1m 05s' },
  { timestamp: new Date(Date.now() - 61 * 60000).toISOString(), type: 'warning', title: 'ddl-auto=create risk flagged', description: 'Detected spring.jpa.hibernate.ddl-auto=create in common application.properties with no environment override — data-loss risk on boot.', agent: 'Security Auditor', duration: '48s' },
  { timestamp: new Date(Date.now() - 95 * 60000).toISOString(), type: 'success', title: 'Local docker-compose stack scaffolded', description: 'Generated docker-compose.local.yml with Postgres, Redis, RabbitMQ, mock essms-auth, mock essms-proxy, and MBO service — no dev/qa/prod connections.', agent: 'Deployment Engineer', duration: '3m 40s' },
  { timestamp: new Date(Date.now() - 142 * 60000).toISOString(), type: 'success', title: 'Integration test suite: multi-tenancy isolation', description: 'Generated MultiTenancyIntegrationTest.java — 8 scenarios covering schema provisioning, cross-tenant admin access, and reserved-name rejection.', agent: 'Test Engineer', duration: '4m 02s' },
  { timestamp: new Date(Date.now() - 190 * 60000).toISOString(), type: 'info', title: 'k6 performance scripts generated', description: 'Created smoke.js, load.js, stress.js targeting 10 MBO endpoints with p95/p99 thresholds and realistic user workflow simulation.', agent: 'Performance Engineer', duration: '2m 51s' },
  { timestamp: new Date(Date.now() - 250 * 60000).toISOString(), type: 'success', title: 'MBO source built via Maven', description: 'bms-mbo-6.0.44.jar built successfully against GitLab package registry (project 79762903), JDK 21.', agent: 'Deployment Engineer', duration: '1m 38s' },
  { timestamp: new Date(Date.now() - 310 * 60000).toISOString(), type: 'warning', title: 'Committed secrets detected', description: 'gitleaks flagged hardcoded DB password and internal.api.secret across application-dev.properties and application.properties.', agent: 'Security Auditor', duration: '22s' },
  { timestamp: new Date(Date.now() - 380 * 60000).toISOString(), type: 'success', title: 'Unit test suite generated for ProjectController + ObjectiveController', description: 'MockMvc-based unit tests covering CRUD, status transitions, access control, and Excel import (24 test methods total).', agent: 'Test Engineer', duration: '3m 15s' },
  { timestamp: new Date(Date.now() - 420 * 60000).toISOString(), type: 'info', title: 'AI SDLC Pipeline Solution Design v0.1 drafted', description: 'Authored 11-section architecture document covering pipeline stages, agent roles, local testing strategy, and success metrics.', agent: 'System Architect', duration: '6m 30s' },
]

export const mockData = {
  services,
  pipeline,
  pipelineHistory,
  scope,
  coverage,
  testHistory,
  security,
  performance,
  architecture,
  recentActivity,
}